import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  MdArrowBack,
  MdCalendarToday,
  MdHome,
  MdLocationCity,
  MdLockOpen,
} from 'react-icons/md';
import { formatDate } from '@kiper/fns';
import { useQuery as useQueryParams } from '@kiper/hooks';
import LoadingPage from '../../../components/LoadingPage';
import InviteError, { InviteErrorTypes } from '../../../components/InviteError';
import { useFetchInviteGuest } from '../InviteGuest/hooks';
import {
  isInviteExpired,
  isInviteValid,
} from '../PersonalizedInvite/adapters/inviteGuestAdapter';
import {
  INVITE_PATHS,
  buildInvitePath,
  getInviteBrandVariant,
} from '../constants';
import { InviteInfoRow } from './components';
import { useOpenGuestSmartLock } from './hooks';
import { DATE_FORMAT, OPEN_STATUS } from './settings';
import * as S from './styles';

const SmartLockOpen = () => {
  const [t] = useTranslation('invite');
  const history = useHistory();
  const querystring = useQueryParams();
  const guestHash = querystring?.get('q')?.replace(/ /g, '+') || '';

  const { data, loading, error } = useFetchInviteGuest({ guestHash });
  const inviteGuest = data?.getInviteGuest;

  const { status, handleOpenSmartLock } = useOpenGuestSmartLock({
    guestHash,
  });

  const invitePath = buildInvitePath(INVITE_PATHS.GUEST, guestHash);
  const unitWithoutSmartLock =
    isInviteValid(inviteGuest) && !inviteGuest.hasSmartLock;

  useEffect(() => {
    if (unitWithoutSmartLock) history.replace(invitePath);
  }, [unitWithoutSmartLock, invitePath]);

  const handleBackToInvite = () => history.push(invitePath);

  if (loading || !guestHash || unitWithoutSmartLock) return <LoadingPage />;

  if (error || !isInviteValid(inviteGuest)) {
    return <InviteError type={InviteErrorTypes.INVALID} />;
  }

  if (isInviteExpired(inviteGuest)) {
    return <InviteError type={InviteErrorTypes.EXPIRED} />;
  }

  const {
    invite,
    condominium,
    unityGroup,
    unity,
    isPorterPartner,
  } = inviteGuest;
  const variant = getInviteBrandVariant(isPorterPartner);
  const unityName = [unityGroup?.name, unity?.name].filter(Boolean).join(' - ');
  const isOpening = status === OPEN_STATUS.LOADING;

  return (
    <S.Wrapper $variant={variant}>
      <S.Content>
        <S.BackButton type="button" onClick={handleBackToInvite}>
          <MdArrowBack size={20} />
          {t('smart-lock.page.back')}
        </S.BackButton>

        <S.Card>
          <S.Header>
            <S.HeaderIcon $variant={variant}>
              <MdLockOpen size={32} />
            </S.HeaderIcon>
            <S.Title>{t('smart-lock.page.title')}</S.Title>
          </S.Header>

          <InviteInfoRow
            icon={<MdHome size={20} />}
            label={t('smart-lock.page.unity')}
          >
            {unityName}
          </InviteInfoRow>
          <InviteInfoRow
            icon={<MdLocationCity size={20} />}
            label={t('smart-lock.page.condominium')}
          >
            {condominium?.name}
          </InviteInfoRow>
          <InviteInfoRow
            icon={<MdCalendarToday size={20} />}
            label={t('smart-lock.page.period')}
          >
            <S.Period>
              <span>{formatDate(invite.start, DATE_FORMAT).formatted}</span>
              <span>{formatDate(invite.end, DATE_FORMAT).formatted}</span>
            </S.Period>
          </InviteInfoRow>

          <S.OpenButton
            $variant={variant}
            icon={<MdLockOpen size={20} />}
            loading={isOpening}
            disabled={isOpening}
            onClick={handleOpenSmartLock}
            centered
          >
            {t('smart-lock.page.open-button')}
          </S.OpenButton>

          {status === OPEN_STATUS.SUCCESS && (
            <S.Feedback $status={status} role="status">
              {t('smart-lock.feedback.success')}
            </S.Feedback>
          )}
          {status === OPEN_STATUS.ERROR && (
            <S.Feedback $status={status} role="alert">
              {t('smart-lock.feedback.error')}
            </S.Feedback>
          )}
        </S.Card>
      </S.Content>
    </S.Wrapper>
  );
};

export default SmartLockOpen;
